import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import oauthService from "../services/oauthService";
import { useAuth } from "../components/AuthWrapper";

export default function Login() {
    const btnRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { status, user, isAdmin, refreshAuth } = useAuth();
    const [error, setError] = useState("");

    useEffect(() => {
        if (isAdmin) {
            navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
            return;
        }

        (async () => {
            try {
                await oauthService.init(async (idToken) => {
                    try {
                        setError("");
                        await oauthService.exchangeIdToken(idToken);
                        await refreshAuth();
                    } catch (e) {
                        setError(e.message || "Authentication failed");
                    }
                });

                oauthService.renderButton(btnRef.current);
                oauthService.promptOneTap();
            } catch (e) {
                setError(e.message || "Failed to initialize Google Sign-In");
            }
        })();
    }, [isAdmin, navigate, location.state]);

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Ambient brand glow */}
            <div className="pointer-events-none absolute inset-0 -z-10" style={{
                background:
                    'radial-gradient(40rem 40rem at 50% -10%, rgba(0,187,143,0.12), transparent 60%),' +
                    'radial-gradient(30rem 30rem at 85% 110%, rgba(72,151,195,0.10), transparent 55%)'
            }} />

            <div className="w-full max-w-md">
                <div className="surface-card shadow-elevated p-8 text-center">
                    {/* Brand mark */}
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary-gradient shadow-glow">
                            <span className="text-on-accent font-bold text-lg leading-none">B</span>
                        </span>
                        <div className="text-left leading-none">
                            <div className="text-lg font-semibold text-slate-900 tracking-tight">BidLyzer</div>
                            <div className="text-[10px] font-mono text-slate-400 uppercase tracking-[0.2em] mt-1">Backstage</div>
                        </div>
                    </div>

                    <h1 className="text-sm font-mono font-bold text-slate-600 uppercase tracking-wide mb-1">
                        Admin Console
                    </h1>
                    <p className="text-slate-500 text-sm mb-6">Sign in to continue</p>

                    {status === "authed" && !isAdmin && (
                        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                            Signed in as <span className="font-mono">{user.email}</span> ({user.role}), which has no
                            admin access. Continue with an admin Google account.
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 animate-fade-in">
                            {error}
                        </div>
                    )}

                    <div ref={btnRef} className="flex items-center justify-center" />
                    <p className="mt-6 text-gray-500 text-xs font-mono">
                        Restricted to authorized company Google accounts.
                    </p>
                </div>
            </div>
        </div>
    );
}
